import React, { useState } from 'react';
import { Plus, MoreVertical, Edit2, Trash2, Leaf, Image as ImageIcon, X } from 'lucide-react';
import { FB_CATEGORIES, FB_ITEMS, FBCategory, FBItem } from '../data/mockData';
export function FBCatalogue() {
  const [activeCategoryId, setActiveCategoryId] = useState(FB_CATEGORIES[0].id);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FBItem | null>(null);
  const activeCategoryItems = FB_ITEMS.filter(item => item.categoryId === activeCategoryId);
  const handleEditItem = (item: FBItem) => {
    setEditingItem(item);
    setIsDrawerOpen(true);
  };
  const handleAddItem = () => {
    setEditingItem(null);
    setIsDrawerOpen(true);
  };
  const DietaryBadge = ({
    type
  }: {
    type: FBItem['dietaryType'];
  }) => {
    if (type === 'VEGAN') {
      return <div className="flex items-center justify-center w-4 h-4 rounded bg-green-100 text-green-700" title="Vegan" data-id="element-271">
          <Leaf className="w-3 h-3" data-id="element-272" />
        </div>;
    }
    const isVeg = type === 'VEG';
    return <div className={`flex items-center justify-center w-4 h-4 rounded border ${isVeg ? 'border-green-600' : 'border-red-600'}`} title={isVeg ? 'Vegetarian' : 'Non-Vegetarian'} data-id="element-273">
        <div className={`w-2 h-2 rounded-full ${isVeg ? 'bg-green-600' : 'bg-red-600'}`} data-id="element-274" />
      </div>;
  };
  return <div className="h-[calc(100vh-12rem)] flex flex-col md:flex-row gap-6 relative overflow-hidden" data-id="element-275">
      {/* Left Panel: Categories */}
      <div className="w-full md:w-72 flex flex-col bg-white rounded-xl border border-gray-200 soft-shadow overflow-hidden flex-shrink-0" data-id="element-276">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-277">
          <h2 className="font-bold text-gray-900" data-id="element-278">Categories</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-279">
          {FB_CATEGORIES.map(category => {
          const itemCount = FB_ITEMS.filter(i => i.categoryId === category.id).length;
          const isActive = activeCategoryId === category.id;
          return <div key={category.id} onClick={() => setActiveCategoryId(category.id)} className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all border-l-4 ${isActive ? 'bg-red-50 border-l-red-600' : 'border-l-transparent hover:bg-gray-50'}`} style={isActive ? {
            borderLeftColor: 'var(--accent-color)',
            backgroundColor: 'color-mix(in srgb, var(--accent-color) 5%, transparent)'
          } : {}} data-id="element-280">
                <div className="flex items-center gap-3" data-id="element-281">
                  <span className={`font-medium ${isActive ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-282">
                    {category.name}
                  </span>
                  <span className="text-[10px] font-bold bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full" data-id="element-283">
                    {itemCount}
                  </span>
                </div>
                <button className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-284">
                  <MoreVertical className="w-4 h-4" data-id="element-285" />
                </button>
              </div>;
        })}
        </div>
        <div className="p-4 border-t border-gray-200" data-id="element-286">
          <button className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-287">
            <Plus className="w-4 h-4" data-id="element-288" />
            Add Category
          </button>
        </div>
      </div>

      {/* Right Panel: Item Grid */}
      <div className="flex-1 overflow-y-auto bg-white rounded-xl border border-gray-200 soft-shadow p-6" data-id="element-289">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-290">
          {activeCategoryItems.map(item => <div key={item.id} className="group flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all relative" data-id="element-291">
              {/* Image Placeholder */}
              <div className="aspect-square bg-gray-100 flex items-center justify-center relative overflow-hidden" data-id="element-292">
                {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-293" /> : <ImageIcon className="w-8 h-8 text-gray-300" data-id="element-294" />}
                <div className="absolute top-3 right-3" data-id="element-295">
                  <DietaryBadge type={item.dietaryType} data-id="element-296" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col flex-1" data-id="element-297">
                <div className="flex justify-between items-start mb-2" data-id="element-298">
                  <h3 className="font-bold text-gray-900 line-clamp-2" data-id="element-299">
                    {item.name}
                  </h3>
                </div>
                <p className="text-lg font-semibold text-gray-900 mb-3" data-id="element-300">
                  ₹{item.price}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-301">
                  {item.allergens.map(allergen => <span key={allergen} className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded" data-id="element-302">
                      {allergen}
                    </span>)}
                </div>

                <div className="mt-auto flex items-center justify-between" data-id="element-303">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${item.status === 'IN_STOCK' ? 'bg-green-100 text-green-700' : item.status === 'LOW_STOCK' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-304">
                    {item.status === 'LOW_STOCK' && '⚠️ '}
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Hover Actions */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[2px]" data-id="element-305">
                <button onClick={() => handleEditItem(item)} className="p-2 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-colors shadow-sm" title="Edit Item" data-id="element-306">
                  <Edit2 className="w-4 h-4" data-id="element-307" />
                </button>
                <button className="p-2 bg-white text-red-600 rounded-full hover:bg-red-50 transition-colors shadow-sm" title="Deactivate Item" data-id="element-308">
                  <Trash2 className="w-4 h-4" data-id="element-309" />
                </button>
              </div>
            </div>)}

          {/* Add Item Card */}
          <button onClick={handleAddItem} className="flex flex-col items-center justify-center aspect-square sm:aspect-auto bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl hover:border-gray-400 hover:bg-gray-100 transition-all text-gray-500 hover:text-gray-700 min-h-[300px]" data-id="element-310">
            <Plus className="w-8 h-8 mb-2" data-id="element-311" />
            <span className="font-medium" data-id="element-312">Add New Item</span>
          </button>
        </div>
      </div>

      {/* Slide-in Drawer */}
      {isDrawerOpen && <>
          <div className="fixed inset-0 bg-black/20 z-40 transition-opacity" onClick={() => setIsDrawerOpen(false)} data-id="element-313" />
          <div className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 translate-x-0" data-id="element-314">
            <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-315">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-316">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              <button onClick={() => setIsDrawerOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-317">
                <X className="w-5 h-5" data-id="element-318" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-319">
              {/* Section 1: Core Details */}
              <section className="space-y-4" data-id="element-320">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-321">
                  Core Details
                </h3>

                <div data-id="element-322">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-323">
                    Item Name
                  </label>
                  <input type="text" defaultValue={editingItem?.name} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none" placeholder="e.g. Classic Salted Popcorn" data-id="element-324" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-325">
                  <div data-id="element-326">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-327">
                      Category
                    </label>
                    <select defaultValue={editingItem?.categoryId || activeCategoryId} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none bg-white" data-id="element-328">
                      {FB_CATEGORIES.map(c => <option key={c.id} value={c.id} data-id="element-329">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-330">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-331">
                      Price (₹)
                    </label>
                    <input type="number" defaultValue={editingItem?.price} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none" placeholder="0.00" data-id="element-332" />
                  </div>
                </div>

                <div data-id="element-333">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-334">
                    Dietary Type
                  </label>
                  <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-335">
                    {['VEG', 'NON_VEG', 'VEGAN'].map(type => <button key={type} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${(editingItem?.dietaryType || 'VEG') === type ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-336">
                        {type.replace('_', ' ')}
                      </button>)}
                  </div>
                </div>

                <div data-id="element-337">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-338">
                    Description
                  </label>
                  <textarea defaultValue={editingItem?.description} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none resize-none" placeholder="Brief description of the item..." data-id="element-339" />
                </div>
              </section>

              {/* Section 2: Images */}
              <section className="space-y-4" data-id="element-340">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-341">
                  Images
                </h3>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-342">
                  <ImageIcon className="w-8 h-8 text-gray-400 mb-2" data-id="element-343" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-344">
                    Click or drag images here
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-345">
                    Up to 5 images, max 5MB each
                  </p>
                </div>
              </section>

              {/* Section 3: Nutrition */}
              <section className="space-y-4" data-id="element-346">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-347">
                  Nutrition (Optional)
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-348">
                  <div className="col-span-2" data-id="element-349">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-350">
                      Serving Size
                    </label>
                    <input type="text" defaultValue={editingItem?.nutrition?.servingSize} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 100g" data-id="element-351" />
                  </div>
                  <div data-id="element-352">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-353">
                      Calories (kcal)
                    </label>
                    <input type="number" defaultValue={editingItem?.nutrition?.calories} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-id="element-354" />
                  </div>
                  <div data-id="element-355">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-356">
                      Protein (g)
                    </label>
                    <input type="number" defaultValue={editingItem?.nutrition?.protein} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-id="element-357" />
                  </div>
                </div>
              </section>

              {/* Section 4: Allergens */}
              <section className="space-y-4" data-id="element-358">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-359">
                  Allergens
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-360">
                  {['Gluten', 'Dairy', 'Eggs', 'Nuts', 'Peanuts', 'Soy', 'Shellfish', 'Fish'].map(allergen => <label key={allergen} className="flex items-center gap-2 cursor-pointer" data-id="element-361">
                      <input type="checkbox" defaultChecked={editingItem?.allergens.includes(allergen as any)} className="rounded border-gray-300 text-accent focus:ring-accent" style={{
                  color: 'var(--accent-color)'
                }} data-id="element-362" />
                      <span className="text-sm text-gray-700" data-id="element-363">{allergen}</span>
                    </label>)}
                </div>
              </section>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3" data-id="element-364">
              <button onClick={() => setIsDrawerOpen(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-365">
                Cancel
              </button>
              <button onClick={() => setIsDrawerOpen(false)} className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity" style={{
            backgroundColor: 'var(--accent-color)'
          }} data-id="element-366">
                Save Item
              </button>
            </div>
          </div>
        </>}
    </div>;
}